import { useEffect, useMemo } from 'react'
import { ContentMetadataText, hasMetadataMath } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { createPortal } from 'react-dom'
import { AlertTriangle, CheckCircle2, X } from 'lucide-react'
import { QuizPreviewPanel } from '@/components/inspection/QuizPreviewPanel'
import type { LessonAssociationRow } from '@/pages/quizSetAssociationHierarchy'
import type { QuizGroupItem } from '@/types/quizSet'
import {
  groupQuizItemsForPreview,
  QUIZ_GROUP_PREVIEW_TYPES,
  type QuizGroupPreviewType,
} from './quizGroupPreviewModel'

const LANE_STYLES: Record<QuizGroupPreviewType, {
  header: string
  badge: string
  border: string
}> = {
  Q: {
    header: 'bg-blue-50/90 text-blue-900',
    badge: 'bg-blue-600 text-white',
    border: 'border-blue-200',
  },
  S: {
    header: 'bg-rose-50/90 text-rose-900',
    badge: 'bg-rose-600 text-white',
    border: 'border-rose-200',
  },
  QQS: {
    header: 'bg-amber-50/90 text-amber-900',
    badge: 'bg-amber-500 text-white',
    border: 'border-amber-200',
  },
}

interface Props {
  row: LessonAssociationRow
  onClose: () => void
}

export function QuizGroupPreviewModal({ row, onClose }: Props) {
  const buckets = useMemo(
    () => groupQuizItemsForPreview(row.association.group.items),
    [row.association.group.items],
  )

  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose])

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="quiz-group-preview-title"
        className="flex max-h-[94vh] w-full max-w-[1680px] flex-col overflow-hidden rounded-2xl border border-white/40 bg-slate-100 shadow-2xl"
      >
        <header className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-200 bg-white px-6 py-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-slate-900 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-white">
                정답 비교 프리뷰
              </span>
              <span className="font-mono text-xs text-slate-400">group #{row.association.group.id}</span>
            </div>
            <h2 id="quiz-group-preview-title" className="mt-2 truncate text-lg font-bold text-slate-900">
              {row.association.group.name}
            </h2>
            <p className={`mt-1 text-sm text-slate-500 ${hasMetadataMath(row.course.title) || hasMetadataMath(row.module.title) || hasMetadataMath(row.lesson.title) ? 'break-words' : 'truncate'}`}>
              <ContentMetadataText text={row.course.title} /> · <ContentMetadataText text={row.module.title} /> · <ContentMetadataText text={row.lesson.title} />
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="문제 그룹 프리뷰 닫기"
            className="rounded-lg border border-slate-200 bg-white p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="flex shrink-0 flex-wrap items-center gap-x-5 gap-y-2 border-b border-slate-200 bg-white/80 px-6 py-3 text-xs text-slate-600">
          <span className="inline-flex items-center gap-1.5 font-semibold text-emerald-700">
            <CheckCircle2 className="h-4 w-4" /> 정답·해설 즉시 노출
          </span>
          <span>문제를 풀거나 채점하지 않는 읽기 전용 검수 화면입니다.</span>
          <span className="ml-auto font-mono text-slate-400">Esc 닫기</span>
        </div>

        <main className="min-h-0 flex-1 overflow-auto p-4">
          <div className="grid min-w-[1260px] grid-cols-3 items-start gap-4">
            {QUIZ_GROUP_PREVIEW_TYPES.map((type) => (
              <PreviewLane key={type} type={type} items={buckets[type]} />
            ))}
          </div>

          {buckets.other.length > 0 ? (
            <section className="mt-4 min-w-[1260px] rounded-xl border border-rose-200 bg-rose-50 p-4">
              <div className="flex items-center gap-2 text-sm font-bold text-rose-800">
                <AlertTriangle className="h-4 w-4" /> 기타 type {buckets.other.length}개
              </div>
              <div className="mt-3 grid grid-cols-3 gap-4">
                {buckets.other.map((item) => (
                  <QuizPreviewCard key={getItemKey(item)} item={item} />
                ))}
              </div>
            </section>
          ) : null}
        </main>
      </section>
    </div>,
    document.body,
  )
}

function PreviewLane({ type, items }: { type: QuizGroupPreviewType; items: QuizGroupItem[] }) {
  const style = LANE_STYLES[type]
  return (
    <section className={`overflow-hidden rounded-xl border bg-white shadow-sm ${style.border}`}>
      <header className={`sticky top-0 flex items-center justify-between gap-3 border-b px-4 py-3 ${style.border} ${style.header}`}>
        <div className="flex items-center gap-2">
          <span className={`inline-flex min-w-11 justify-center rounded-md px-2 py-1 text-xs font-black ${style.badge}`}>
            {type}
          </span>
          <span className="text-sm font-bold">{type} 문제</span>
        </div>
        <span className="text-xs font-semibold tabular-nums opacity-70">{items.length}개</span>
      </header>

      <div className="flex flex-col gap-3 p-3">
        {items.length === 0 ? (
          <div className="flex min-h-32 items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-400">
            연결된 {type} 문제가 없습니다
          </div>
        ) : items.map((item) => (
          <QuizPreviewCard key={getItemKey(item)} item={item} />
        ))}
      </div>
    </section>
  )
}

function QuizPreviewCard({ item }: { item: QuizGroupItem }) {
  return (
    <article className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-slate-50/80 px-3 py-2">
        <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[11px] font-black text-slate-700">{item.type}</span>
        <span className="font-mono text-[11px] tabular-nums text-slate-500">#{item.quiz.quizId}</span>
      </header>
      <div className="p-3">
        <QuizPreviewPanel quizId={item.quiz.quizId} revealOnly />
      </div>
    </article>
  )
}

function getItemKey(item: QuizGroupItem): string {
  return `${item.sequenceOrder}-${item.type}-${item.quiz.quizId}`
}
